import { useProjectStore } from '@/core/project/store/project.store'
import { useCharactersStore } from '@/features/shared/stores/characters.store'
import { CharacterForm } from '../components/CharacterForm'
import { useCharacters } from '../queries/character.queries'
import { Loader2, Users, UserPlus } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { WorkspaceLayout } from '@/features/workspace'
import { SpEditorProvider } from '@/features/screenplay/components/editor/SpEditorProvider'
import ChSidebar from '../components/ChSidebar'
import ChOptionBar from '../components/ChOptionBar'
import HeadlessSpEditor from '@/features/shared/component/HeadlessSpEditor'

import { useMemo, useCallback } from 'react'

const CharacterProviderWrapper = ({ children }: { children: React.ReactNode }) => (
    <SpEditorProvider>{children}</SpEditorProvider>
)

export default function CharacterPage() {
    const { project } = useProjectStore()
    const { activeCharacterId, characters, setActiveCharacterId } = useCharactersStore()
    const { isLoading } = useCharacters(project?.id || '')

    const activeCharacter = useMemo(
        () => characters.find(c => c.id === activeCharacterId),
        [characters, activeCharacterId]
    )

    const handleClose = useCallback(() => {
        setActiveCharacterId(undefined)
    }, [setActiveCharacterId])

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        )
    }

    return (
        <WorkspaceLayout
            ProviderWrapper={CharacterProviderWrapper}
            SidebarComponent={<ChSidebar />}
            optionBar={<ChOptionBar />}
        >
            <HeadlessSpEditor />
            <div className="flex-1 flex flex-col h-full bg-muted/10 overflow-hidden">
                {/* Header Area */}
                <div className="p-8 pb-4 shrink-0">
                    <div className="max-w-4xl mx-auto flex items-center justify-between mb-8">
                        <div>
                            <h1 className="text-4xl font-extrabold tracking-tight flex items-center gap-3">
                                <Users className="w-10 h-10 text-primary" />
                                Characters
                            </h1>
                            <p className="text-muted-foreground mt-2 text-lg">
                                Manage the cast and personalities of your story.
                            </p>
                        </div>
                        {activeCharacterId === undefined && characters.length > 0 && (
                            <Button onClick={() => setActiveCharacterId(null)} className="shadow-lg">
                                <UserPlus className="w-4 h-4 mr-2" />
                                New Character
                            </Button>
                        )}
                    </div>

                    <div className="max-w-4xl mx-auto">
                        {activeCharacterId !== undefined && (
                            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                                <CharacterForm
                                    character={activeCharacter}
                                    projectId={project?.id || ''}
                                    onClose={handleClose}
                                />
                            </div>
                        )}

                        {activeCharacterId === undefined && characters.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed rounded-xl bg-background/50">
                                <Users className="w-16 h-16 text-muted-foreground/20 mb-4" />
                                <h3 className="text-xl font-semibold">No characters yet</h3>
                                <p className="text-muted-foreground mb-6">Start by adding your first character or import them from your screenplay.</p>
                                <Button onClick={() => setActiveCharacterId(null)}>
                                    <UserPlus className="w-4 h-4 mr-2" />
                                    Add First Character
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Grid view only if no character is active in form */}
                {activeCharacterId === undefined && characters.length > 0 && (
                    <div className="p-8 pt-0 max-w-4xl mx-auto w-full overflow-y-auto min-h-0">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pb-8">
                            {characters.map(char => (
                                <button
                                    key={char.id}
                                    onClick={() => setActiveCharacterId(char.id)}
                                    className="flex flex-col text-left p-4 rounded-xl border bg-background hover:border-primary hover:shadow-md transition-all group"
                                >
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                                            <Users className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold truncate">{char.name}</h4>
                                            <span className="text-[10px] text-muted-foreground uppercase tracking-widest">{char.role || 'Support'}</span>
                                        </div>
                                    </div>
                                    {char.description && (
                                        <p className="text-xs text-muted-foreground line-clamp-2 italic">
                                            "{char.description}"
                                        </p>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </WorkspaceLayout>
    )
}
